import axios, { AxiosError } from "axios";

import { toastify } from "../components/common/notification";

import { url } from ".";

import { LogParam } from "../types/logParam";
import { logError, logInfo } from "../utils/logger";
import { catchHandler } from "../utils";
import { apiUnauthLogout } from "./common";

// always set function name and message for every executable block
// data is optional
const logParams: LogParam = {
  applicationName: "Orchestrator Client",
  screenModuleName: "client\\src\\controllers\\task.ts",
  functionName: "",
  message: "",
};

export type ConfigT = {
  id: string;
  value: { p: string; v: string; to?: string; vt: string }[];
};

export type TaskT = {
  _id: string;
  name: string;
  organization: { _id: string; name: string };
  created_by: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  totalVersions: number;
};

// export type TasksT = TaskT[];

// export type PaginationT = {
//   currentPage: number;
//   numberofRecordsPerPage: number;
//   totalPages: number;
//   totalRecords: number;
// };

// export type TaskListAPIResponseT = {
//   tasks: TasksT;
//   pagination: PaginationT;
// };

export type TaskItemT = {
  _id: string;
  name: string;
  description: string;
  manual_hour: string;
  versions: {
    version: number;
    flow: string;
    createdBy: {
      _id: string;
      name: string;
      email: string;
    };
    baseVersion: number;
    description?: string;
    startId: string;
    studioVersion: string;
    _id: string;
    createdAt: string;
    updatedAt: string;
  }[];
  organization: string;
  created_by: {
    _id: string;
    name: string;
    email: string;
  };
  active_status: boolean;
  library: boolean;
  deleted_status: boolean;
  granted_agent_admins: string[];
  granted_developers: string[];
  createdAt: string;
  updatedAt: string;
  __v: number;
};

const apiErrorHandler = (
  error: AxiosError<{ message: string }>,
  functionName: string
) => {
  let errorMsg = "Something went wrong";
  if (error.response?.data.message) {
    errorMsg = error.response.data.message;
  }
  logParams.functionName = `apiErrorHandler : ${functionName}`;
  logParams.message = errorMsg;
  logError(logParams);

  toastify("failure", errorMsg);
};

export const taskListApi = (
  token: string,
  queryParams: { q?: string; page?: number } = {}
) => {
  const params = Object.entries(queryParams).reduce<string[][]>(
    (acc, entry) => {
      return [...acc, [entry[0].toString(), entry[1].toString()]];
    },
    []
  );
  const urlSearchParams = new URLSearchParams(params).toString();
  const searchParamString = urlSearchParams && `?${urlSearchParams}`;
  return axios
    .get<{ data: TaskT[]; max_task_version: number }>(
      `${url}/task${searchParamString}`,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      logParams.functionName = "taskListApi";
      logParams.message = `retrieved task(s) with search filter ${searchParamString}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "taskListApi"));
};

export const getTaskApi = (token: string, taskId: string) => {
  return axios
    .get<{ data: [TaskItemT | undefined] }>(`${url}/task/${taskId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "getTaskApi";
      logParams.message = `retrieved task ${taskId} by id`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "getTaskApi"));
};

export const getAgentTasksSchedule = (
  token: string,
  agentId: string,
  orgId: string
) => {
  return axios
    .get<{ data: any }>(`${url}/agent/${agentId}/org/${orgId}/viewSchedule`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "getAgentTasksSchedule";
      logParams.message = `retrieved tasks schedule for ${agentId} by id`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "getAgentTasksSchedule"));
};

export const organizationTaskListInfoApi = (token: string, orgId: string) =>
  axios
    .get(`${url}/task/org-taskInfo/${orgId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationTaskListApi";
      logParams.message = `retrieved task(s) for org ${orgId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "organizationTaskListApi"));

export const organizationTaskListApi = (token: string, orgId: string) =>
  axios
    .get(`${url}/task/org/${orgId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationTaskListApi";
      logParams.message = `retrieved task(s) for org ${orgId}`;
      logInfo(logParams);
      return res.data;
    })
    .catch((error) => {
      if (error.response.status == 401) {
        apiUnauthLogout(error, "organizationTaskListApi");
      } else {
        apiErrorHandler(error, "organizationTaskListApi");
      }
    });

export const botTaskListApi = (token: string, botId: string) =>
  axios
    .get(`${url}/task/bot/${botId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "botTaskListApi";
      logParams.message = `retrieved task(s) for bot ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      if (error.response.status == 401) {
        apiUnauthLogout(error, "botTaskListApi");
      } else {
        apiErrorHandler(error, "botTaskListApi");
      }
    });

export const botTaskLastRunLogApi = (token: string, botId: string) =>
  axios
    .get(`${url}/task/last-run-log/${botId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "botTaskLastRunLogApi";
      logParams.message = `retrieved task(s) logs for bot ${botId}`;
      logInfo(logParams);
      return res.data;
    })
    .catch((error) => {
      if (error.response.status == 401) {
        apiUnauthLogout(error, "botTaskLastRunLogApi");
      } else {
        apiErrorHandler(error, "botTaskLastRunLogApi");
      }
    });

export const organizationBotTaskListApi = (token: string, orgId: string) =>
  axios
    .get(`${url}/agent/${orgId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "organizationBotTaskListApi";
      logParams.message = `retrieved bots(s) for org ${orgId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "organizationBotTaskListApi"));

export const schedulerTaskApi = (token: string, data: unknown) =>
  axios
    .post(`${url}/schedule/`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "schedulerTaskApi";
      logParams.message = `created task schedule`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "schedulerTaskApi"));

export const scheduledTaskApi = (token: string, botId: string) =>
  axios
    .get(`${url}/task/bot/${botId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "scheduledTaskApi";
      logParams.message = `retrieved scheduled task(s) for bot ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "scheduledTaskApi"));

export const retrieveScheduledData = (
  token: string,
  taskId: string,
  botId: string
) =>
  axios
    .get<{ data: Record<string, unknown> } | undefined>(
      `${url}/schedule/retrieve-data/${taskId}/${botId}`,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      logParams.functionName = "retrieveScheduleData";
      logParams.message = `retrieved schedule data for task ${taskId} and bot ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => {
      apiErrorHandler(error, "organizationTaskListApi");
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return;
      }
    });

export const agentScheduleData = (
  token: string,
  botId: string,
  orgId: string
) =>
  axios
    .get(`${url}/schedule/organization/${orgId}/agent/${botId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "agentScheduleData";
      logParams.message = `retrieved agent schedule data for org ${orgId} and bot ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "agentScheduleData"));

export const removeScheduleData = (
  token: string,
  id: string,
  taskId: string,
  agentId: string,
  orgId: string
) =>
  axios
    .delete(`${url}/schedule/remove/${id}/${taskId}/${agentId}/${orgId}`, {
      headers: {
        authorization: `Bearer ${token} `,
      },
    })
    .then((res) => {
      logParams.functionName = "removeScheduleData";
      logParams.message = `removed schedule data for task ${taskId}, bot ${agentId} and org ${orgId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "removeScheduleData"));

export const updateScheduledData = (
  token: string,
  data: unknown,
  taskId: string,
  botId: string
) =>
  axios
    .put(`${url}/schedule/${taskId}/${botId}`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "updateScheduledData";
      logParams.message = `updated schedule for task ${taskId} and bot ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "updateScheduledData"));

export const importTaskApi = (token: string, data: unknown, agent: string) =>
  axios
    .put(`${url}/agent/${agent}`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "importTaskApi";
      logParams.message = `imported task`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "importTaskApi"));

export const changeVersionApi = (
  token: string,
  data: unknown,
  orgId: string,
  agentId: string,
  taskId: string,
  versionNo: number
) =>
  axios
    .put(
      `${url}/agent/${orgId}/${agentId}/${taskId}/version/${versionNo}`,
      data,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      logParams.functionName = "changeVersionApi";
      logParams.message = `changed task ${taskId} version number ${versionNo}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "changeVersionApi"));

export const deleteTaskApi = (token: string, agentId: string, taskId: string) =>
  axios
    .delete(`${url}/agent/task/${agentId}/${taskId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "deleteTaskApi";
      logParams.message = `deleted task ${taskId} for bot ${agentId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "deleteTaskApi"));

export const taskDeleteApi = (token: string, taskId: string) =>
  axios
    .delete(`${url}/task/${taskId}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "taskDeleteApi";
      logParams.message = `deleted task ${taskId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "taskDeleteApi"));

export const taskVersionDeleteApi = (
  token: string,
  version: string,
  taskId: string
) =>
  axios
    .delete(`${url}/task/${taskId}/version/${version}`, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "taskVersionDeleteApi";
      logParams.message = `deleted task ${taskId} version ${version}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "taskVersionDeleteApi"));

export const moveTaskApi = (token: string, agentId: string, data: unknown) =>
  axios
    .put(`${url}/agent/task/move/${agentId}`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "moveTaskApi";
      logParams.message = `moved task to bot ${agentId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "moveTaskApi"));

export const importTaskLibraryApi = (
  token: string,
  botId: string,
  data: unknown
) =>
  axios
    .put(`${url}/agent/library/${botId}`, data, {
      headers: {
        authorization: `Bearer ${token} `,
      },
    })
    .then((res) => {
      logParams.functionName = "importTaskLibraryApi";
      logParams.message = `imported task library for agent ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "importTaskLibraryApi"));

export const getTaskConfig = (token: string, botId: string, taskId: string) =>
  axios
    .get(`${url}/agent/task/config/${botId}/${taskId}`, {
      headers: {
        authorization: `Bearer ${token} `,
      },
    })
    .then((res) => {
      logParams.functionName = "getTaskConfig";
      logParams.message = `retrieved task ${taskId}} configuration for bot ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "getTaskConfig"));

export const getAuthDetails = (token: string) =>
  axios
    .get(`${url}/emailAuth/getAuth`, {
      headers: {
        authorization: `Bearer ${token} `,
      },
    })
    .then((res) => {
      logParams.functionName = "getAuthDetails";
      logParams.message = `retrieved authentication details`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "getAuthDetails"));

export const putTaskConfig = (
  token: string,
  botId: string,
  taskId: string,
  data: { config: ConfigT[] }
) =>
  axios
    .put(`${url}/agent/task/config/${botId}/${taskId}`, data, {
      headers: {
        authorization: `Bearer ${token} `,
      },
    })
    .then((res) => {
      logParams.functionName = "putTaskConfig";
      logParams.message = `updated task ${taskId} config for bot ${botId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "putTaskConfig"));

export const putMailConfig = (
  token: string,
  botId: string,
  taskId: string,
  data: unknown
) =>
  axios
    .put(`${url}/agent/task/mail/${botId}/${taskId}`, data, {
      headers: {
        authorization: `Bearer ${token} `,
      },
    })
    .then((res) => {
      logParams.functionName = "putMailConfig";
      logParams.message = `updated mail configuration for bot ${botId} and task ${taskId}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "putMailConfig"));

export const pushTaskInTaskLibrary = (
  token: string,
  data: unknown,
  organization: string
) =>
  axios
    .post(`${url}/task/${organization}`, data, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      logParams.functionName = "pushTaskInTaskLibrary";
      logParams.message = `pushed task in task library for org ${organization}`;
      logInfo(logParams);

      return res.data;
    })
    .catch((error) => apiErrorHandler(error, "pushTaskInTaskLibrary"));

export const stopTask = (token: string, agentId: string, taskId: string) => {
  axios
    .post(
      `${url}/stopTask/agent/${agentId}/task/${taskId}/stop`,
      {},
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      logParams.functionName = "stopTask";
      logParams.message = `Stop task with taskID ${taskId}`;
      logInfo(logParams);
      toastify("success", res.data);
      return res.data;
    })
    .catch((error) => {
      apiErrorHandler(error, "stopTask");
      toastify("failure", "Failed to trigger stop task");
    });
};

export const downloadFiles = async (token: string, version: string, taskName: string) => 
  axios
    .get(
      `${url}/task/${taskName}/version/${version}/downloadFiles`,
      {
        responseType: "arraybuffer",
        headers: {
          authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    )
    .then((res) => {
      console.log(res, "RES")
      logParams.functionName = "downloadFiles";
      logParams.message = `Download Files with taskName ${taskName}`;
      logInfo(logParams);
      toastify("success", "The requested task files has been downloaded successfully");
      return res.data;
    })
    .catch((error) => {
      // apiErrorHandler(error, "downloadFiles");
      let eMsg = "";
      var bytes = new Uint8Array(error.response.data);
      var len = bytes.byteLength;
      for (var i = 0; i < len; i++) {
        eMsg += String.fromCharCode(bytes[i]);
      }

      const errMessage = JSON.parse(eMsg);
      console.log(errMessage);
      toastify("failure", "Failed to download files");
      return "false"
    });

export const taskCheckpoint = (
  token: string,
  orgId: string,
  agentId: string,
  taskId: string
) =>
  axios
    .get(
      `${url}/taskCheckpoint/organization/${orgId}/agent/${agentId}/task/${taskId}/checkpoint`,
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      logParams.functionName = "taskCheckpoint";
      logParams.message = `taskCheckpoint with taskID ${taskId}`;
      logInfo(logParams);
      return res.data;
    })
    .catch((error) => {
      catchHandler(error);
    });

//this is for unattended
export const runTask = (token: string, agentId: string, taskId: string) =>
  axios
    .post(
      `${url}/runTask/agent/${agentId}/task/${taskId}`,
      {},
      {
        headers: {
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      logParams.functionName = "runTask";
      logParams.message = `runTask with taskID ${taskId}`;
      logInfo(logParams);
      return res.data;
    })
    .catch((error) => {
      apiErrorHandler(error, "runTask");
    });
